/**
 * HealthCheckPanel — G007 헬스체크 데모
 * R12 #333
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showcaseApi } from '../api/showcaseApi';
import type { HealthCheckResponse } from '../types/showcase';

const SAMPLE_ROUTES = [
  { value: 'r-001', label: 'r-001 — user-service' },
  { value: 'r-002', label: 'r-002 — order-service' },
  { value: 'r-003', label: 'r-003 — payment-service' },
  { value: 'r-004', label: 'r-004 — notification-service' },
  { value: 'r-005', label: 'r-005 — file-service' },
];

export function HealthCheckPanel() {
  const { t } = useTranslation();
  const [routeId, setRouteId] = useState('r-001');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<HealthCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCall() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await showcaseApi.healthCheckDemo(routeId);
      setResult(res);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  const isUp = result?.status === 'UP';

  return (
    <section aria-labelledby="health-check-panel-title">
      <h2
        id="health-check-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.healthCheck.title')}
      </h2>
      <p
        style={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        G007 — eraf-gateway / eraf-actuator
      </p>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          alignItems: 'flex-end',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div style={{ flex: 1 }}>
          <label
            htmlFor="health-route"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('showcase.healthCheck.routeLabel')}
          </label>
          <select
            id="health-route"
            value={routeId}
            onChange={(e) => setRouteId(e.target.value)}
            aria-label={t('showcase.healthCheck.routeLabel')}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              cursor: 'pointer',
            }}
          >
            {SAMPLE_ROUTES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleCall}
          disabled={loading}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: loading ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
            color: loading ? 'var(--color-text-muted)' : 'var(--color-text-on-brand)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 700,
            fontSize: 'var(--font-size-sm)',
            cursor: loading ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? '...' : t('showcase.healthCheck.callButton')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--spacing-3)',
            background: 'var(--color-status-error-bg)',
            border: '1px solid var(--color-status-error)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-status-error)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {error}
        </div>
      )}

      {/* Result card */}
      {result && (
        <div
          style={{
            border: `1px solid ${isUp ? 'var(--color-status-success)' : 'var(--color-status-error)'}`,
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: isUp ? 'var(--color-status-success-bg)' : 'var(--color-status-error-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {result.routeId}
              </span>
              <span
                style={{
                  marginLeft: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                }}
              >
                {result.routeName}
              </span>
              <span
                style={{
                  marginLeft: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                → {result.service}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <span
                role="status"
                aria-label={`Status: ${result.status}`}
                style={{
                  fontWeight: 700,
                  fontSize: 'var(--font-size-sm)',
                  color: isUp ? 'var(--color-status-success)' : 'var(--color-status-error)',
                }}
              >
                {isUp ? t('showcase.healthCheck.statusUp') : t('showcase.healthCheck.statusDown')}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {t('showcase.healthCheck.durationLabel')}: {result.durationMs}ms
              </span>
            </div>
          </div>

          {/* Components grid */}
          {result.components.length > 0 && (
            <div
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 'var(--spacing-2)',
              }}
            >
              {result.components.map((comp) => (
                <div
                  key={comp.name}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                      {comp.name}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: comp.status === 'UP' ? 'var(--color-status-success)' : 'var(--color-status-error)',
                      }}
                    >
                      {comp.status}
                    </span>
                  </div>
                  {comp.detail && (
                    <p style={{ fontSize: 10, color: 'var(--color-text-muted)', margin: 'var(--spacing-1) 0 0 0', lineHeight: 1.4 }}>
                      {comp.detail}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* External link */}
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        <a
          href="https://eraf.skax.co.kr/actuator/health"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-brand-primary)', textDecoration: 'underline' }}
        >
          {t('showcase.healthCheck.externalLink')} →
        </a>
      </p>
    </section>
  );
}
